import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import styled from 'styled-components';

const ChartWrapper = styled.div`
  width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || 'auto'};
  background-color: ${({ noBg }) => (noBg ? 'transparent' : '#fff')};
  border-radius: ${({ noBorder }) => (noBorder ? '0' : '40px')};
  box-shadow: ${({ noShadow }) => (noShadow ? 'none' : '0px 2px 8px rgba(6, 12, 34, 0.04)')};
  padding: ${({ noPadding }) => (noPadding ? '0' : '20px')};
  margin-bottom: ${({ noMargin }) => (noMargin ? '0' : '20px')};
  border: ${({ noBorder }) => (noBorder ? 'none' : '1px solid #ebeaf2')};
  position: relative;
`;

const chartThemes = {
  light: {
    backgroundColor: 'rgba(75, 192, 192, 0.6)',
    borderColor: 'rgba(75, 192, 192, 1)',
    gridColor: 'rgba(0, 0, 0, 0.1)',
    textColor: '#333',
    fontFamily: 'Arial, sans-serif',
  },
  dark: {
    backgroundColor: 'rgba(75, 192, 192, 0.6)',
    borderColor: 'rgba(75, 192, 192, 1)',
    gridColor: 'rgba(255, 255, 255, 0.1)',
    textColor: '#eee',
    fontFamily: 'Arial, sans-serif',
  },
  custom: {
    backgroundColor: ['rgba(74, 85, 70, 0.6)', 'rgba(255, 175, 50, 0.6)'],
    borderColor: ['rgba(74, 85, 70, 1)', 'rgba(255, 175, 50, 1)'],
    gridColor: 'rgba(0, 0, 0, 0.05)',
    textColor: '#4a5546',
    fontFamily: 'Arial, sans-serif',
  },
};

const hasChartData = (labels, datasets) => {
  if (!labels?.length || !datasets?.length) return false;

  return datasets.some(ds =>
    Array.isArray(ds.data) &&
    ds.data.some(value => value !== null && value !== undefined && value !== 0)
  );
};

const NoDataOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
  color: #9aa0a6;
  background: transparent;
  pointer-events: none;
`;


const defaultOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
    },
    tooltip: {
      mode: 'index',
      intersect: false,
    },
  },
  scales: {
    x: {
      grid: {
        display: true,
        color: chartThemes.light.gridColor,
      },
      ticks: {
        color: chartThemes.light.textColor,
      },
    },
    y: {
      beginAtZero: true,
      grid: {
        display: true,
        color: chartThemes.light.gridColor,
      },
      ticks: {
        color: chartThemes.light.textColor,
      },
    },
  },
};

const createChartConfig = (
  type,
  labels,
  datasets,
  title,
  theme,
  legendPosition,
  horizontal = false 
) => {
  const currentTheme = chartThemes[theme] || chartThemes.light;

  const themedDatasets = datasets.map((dataset, index) => {
    if (type === 'pie' || type === 'doughnut') {
      return {
        backgroundColor: Array.isArray(currentTheme.backgroundColor)
          ? currentTheme.backgroundColor.map((color, i) =>
              color.replace(/[\d.]+\)$/, `${0.6 + i * 0.1})`)
            )
          : [currentTheme.backgroundColor],
        borderColor: Array.isArray(currentTheme.borderColor)
          ? currentTheme.borderColor
          : [currentTheme.borderColor],
        borderWidth: 1,
        ...dataset,
      };
    }

    return {
      backgroundColor: Array.isArray(currentTheme.backgroundColor)
        ? currentTheme.backgroundColor[index % currentTheme.backgroundColor.length]
        : currentTheme.backgroundColor,
      borderColor: Array.isArray(currentTheme.borderColor)
        ? currentTheme.borderColor[index % currentTheme.borderColor.length]
        : currentTheme.borderColor,
      borderWidth: type === 'line' ? 2 : 1,
      tension: type === 'line' ? 0.3 : 0,
      ...dataset,
    };
  });

  const config = {
    type,
    data: {
      labels,
      datasets: themedDatasets,
    },
    options: {
      ...defaultOptions,
      indexAxis: horizontal ? 'y' : 'x', // 👈 SET HORIZONTAL AXIS
      plugins: {
        ...defaultOptions.plugins,
        legend: {
          ...defaultOptions.plugins.legend,
          position: legendPosition,
          labels: {
            boxWidth: 20,
            padding: 15,
            usePointStyle: true,
            font: {
              family: currentTheme.fontFamily,
            },
            color: currentTheme.textColor,
          },
        },
        title: {
          display: !!title,
          text: title,
          font: {
            size: 16,
            family: currentTheme.fontFamily,
          },
          color: currentTheme.textColor,
        },
      },
      scales: type === 'pie' || type === 'doughnut' ? {} : {
        x: {
          grid: {
            display: true,
            color: currentTheme.gridColor,
          },
          ticks: {
            color: currentTheme.textColor,
            callback: function (value) {
              const label = this.getLabelForValue(value);
              return label.length > 12 ? label.slice(0, 12) + '…' : label;
            },
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            display: true,
            color: currentTheme.gridColor,
          },
          ticks: {
            color: currentTheme.textColor,
          },
        },
      },
    },
  };

  if (type === 'pie' || type === 'doughnut') {
    delete config.options.scales;
  }

  return config;
};


// const BaseChart = ({
//   type,
//   labels = [],
//   datasets = [],
//   title,
//   theme = 'custom',
//   height = '300px',
//   width = '100%',
//   legendPosition = 'top',
//   horizontal = false, 
//   noBorder = false,
//   noShadow = false,
//   noBg = false,
//   noPadding = false,
//   noMargin = false,
// }) => {
//   const chartRef = useRef(null);
//   const chartInstanceRef = useRef(null);

//   useEffect(() => {
//     if (chartRef.current && labels.length > 0 && datasets.length > 0) {
//       const ctx = chartRef.current.getContext('2d');
//       if (chartInstanceRef.current) chartInstanceRef.current.destroy();
//       const config = createChartConfig(
//         type,
//         labels,
//         datasets,
//         title,
//         theme,
//         legendPosition,
//         horizontal // 👈 PASS IT IN
//       );
//       chartInstanceRef.current = new Chart(ctx, config);
//     }

//     return () => {
//       if (chartInstanceRef.current) {
//         chartInstanceRef.current.destroy();
//       }
//     };
//   }, [type, labels, datasets, title, theme, legendPosition, horizontal]);

//   return (
//     <ChartWrapper 
//       width={width}
//       height={height}
//       noBorder={noBorder}
//       noShadow={noShadow}
//       noBg={noBg}
//       noPadding={noPadding}
//       noMargin={noMargin}
//     >
//       <canvas ref={chartRef} />
//     </ChartWrapper>
//   );
// };


const BaseChart = ({
  type,
  labels = [],
  datasets = [],
  title,
  theme = 'custom',
  height = '300px',
  width = '100%',
  legendPosition = 'top',
  horizontal = false,
  noBorder = false,
  noShadow = false,
  noBg = false,
  noPadding = false,
  noMargin = false,
}) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const showChart = hasChartData(labels, datasets);

  useEffect(() => {
    if (!showChart) return;

    if (chartRef.current) {
      const ctx = chartRef.current.getContext('2d');
      if (chartInstanceRef.current) chartInstanceRef.current.destroy();

      const config = createChartConfig(
        type,
        labels,
        datasets,
        title,
        theme,
        legendPosition,
        horizontal
      );

      chartInstanceRef.current = new Chart(ctx, config);
    }

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [type, labels, datasets, title, theme, legendPosition, horizontal, showChart]);

  return (
    <ChartWrapper
      width={width}
      height={height}
      noBorder={noBorder}
      noShadow={noShadow}
      noBg={noBg}
      noPadding={noPadding}
      noMargin={noMargin}
    >
      {showChart ? (
        <canvas ref={chartRef} />
      ) : (
        <NoDataOverlay>No data available</NoDataOverlay>
      )}
    </ChartWrapper>
  );
};



const LineChart = (props) => <BaseChart type="line" {...props} />;
const BarChart = (props) => <BaseChart type="bar" {...props} />;
const PieChart = (props) => <BaseChart type="pie" {...props} />;
const DonutChart = (props) => {
  const modifiedDatasets = props.datasets?.map((dataset) => ({
    ...dataset,
    cutout: '70%',
  })) || [];
  return <BaseChart type="doughnut" {...props} datasets={modifiedDatasets} />;
};

export { BaseChart, LineChart, BarChart, PieChart, DonutChart };